import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, ShoppingBag, ExternalLink, Sparkles, Loader2,
  ShieldCheck, Bath, Toilet, Bed, Watch, Pill, BedDouble, Puzzle, Clock, NotebookPen, Move,
  Heart, Home, Utensils,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon-name → Lucide component (admin picks by string name)
const ICON_MAP = {
  ShoppingBag, ShieldCheck, Bath, Toilet, Bed, BedDouble, Watch, Pill,
  Puzzle, Clock, NotebookPen, Move, Heart, Home, Utensils,
};

const categories = [
  { id: "all", label: "All" },
  { id: "safety", label: "Home Safety" },
  { id: "mobility", label: "Mobility & Fall Prevention" },
  { id: "bathroom", label: "Bathroom & Toileting" },
  { id: "comfort", label: "Comfort & Sleep" },
  { id: "cognition", label: "Cognition & Engagement" },
  { id: "caregiver", label: "For the Caregiver" },
];

export default function ShoppingPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/extras/products`)
      .then((res) => setProducts(res.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeCategory === "all"
    ? products
    : products.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="shopping-back-btn"
              onClick={() => navigate("/")}
              variant="ghost"
              className="text-primary hover:bg-primary/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <button
              data-testid="shopping-brand-home"
              onClick={() => navigate("/get-started")}
              className="flex items-center gap-3"
              aria-label="Explore care types"
            >
              <img
                src="/tyvena-lockup-horizontal.png"
                alt="Tyvena"
                className="h-10 md:h-11 w-auto rounded-lg shadow-md"
              />
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/20 rounded-full mb-6">
          <Sparkles className="w-4 h-4 text-secondary" />
          <span className="text-sm font-body font-semibold text-text-primary">
            Curated by Tyvena
          </span>
        </div>
        <div className="flex items-start gap-4 md:gap-6">
          <div className="hidden md:flex flex-shrink-0 w-16 h-16 rounded-2xl bg-primary/10 items-center justify-center">
            <ShoppingBag className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-primary leading-tight mb-4">
              Recommended Shopping
            </h1>
            <p className="text-lg text-text-secondary font-body max-w-2xl leading-relaxed">
              The little things that make daily care safer, easier, and more dignified.
              Every item here has been recommended by home health nurses, occupational
              therapists, or families who've been in the trenches.
            </p>
          </div>
        </div>
      </section>

      {/* Category chips */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`category-${c.id}`}
              onClick={() => setActiveCategory(c.id)}
              className={`px-4 py-2 rounded-full font-body font-semibold text-sm transition-all border-2 ${
                activeCategory === c.id
                  ? "bg-primary text-white border-primary shadow-md"
                  : "bg-white text-text-secondary border-[#E2E8F0] hover:border-primary hover:text-primary"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-text-secondary font-body py-16">No products in this category.</p>
        ) : (
          <div data-testid="shopping-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => {
              const Icon = ICON_MAP[p.icon_name] || ShoppingBag;
              const tintFrom = p.tint_from || "#2AADA0";
              const tintTo = p.tint_to || "#238C82";
              return (
                <a
                  key={p.id}
                  data-testid={`product-${p.id}`}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-2xl border border-[#E2E8F0] shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all overflow-hidden flex flex-col"
                >
                  <div
                    className="relative h-40 overflow-hidden flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${tintFrom}, ${tintTo})` }}
                  >
                    <Icon className="w-20 h-20 text-white/90 group-hover:scale-110 transition-transform duration-500" strokeWidth={1.5} />
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/95 text-[11px] font-body font-bold text-text-primary uppercase tracking-wider shadow-sm">
                      {categories.find((c) => c.id === p.category)?.label || p.category}
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="text-lg font-heading font-semibold text-text-primary mb-2 leading-snug">
                      {p.name}
                    </h3>
                    <p className="text-sm text-text-secondary font-body leading-relaxed mb-4 flex-1">
                      {p.description}
                    </p>
                    <div className="flex items-center justify-between gap-4 mt-auto pt-3 border-t border-[#E2E8F0]">
                      {p.price && <span className="text-sm font-body font-bold text-secondary">{p.price}</span>}
                      <span className="text-sm font-body font-semibold text-primary flex items-center gap-1.5 group-hover:gap-2 transition-all ml-auto">
                        Shop
                        <ExternalLink className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}

        <p className="text-xs text-text-secondary font-body text-center mt-10 max-w-2xl mx-auto">
          Product links go to Amazon or the manufacturer. <strong>As an Amazon Associate,
          Tyvena earns from qualifying purchases</strong> — at no extra cost to you. Items are
          here because home health nurses keep recommending them. Prices are approximate.
        </p>
      </section>
    </div>
  );
}
